import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, ChevronRight, CheckCircle, XCircle, Award, AlertCircle } from 'lucide-react';

const Webdevquiz = () => {
  const { courseName } = useParams();
  const navigate = useNavigate();
  const decodedCourseName = decodeURIComponent(courseName);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(30);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [answersReview, setAnswersReview] = useState([]);

  // Quiz Questions for Web Development
  const questions = [
    {
      id: 1,
      text: "What does HTML stand for?",
      options: [
        "Hyper Text Markup Language",
        "High Tech Modern Language",
        "Hyper Transfer Markup Language",
        "Home Tool Markup Language"
      ],
      correct: 0
    },
    {
      id: 2,
      text: "Which CSS property is used to change the text color of an element?",
      options: [
        "text-color",
        "color",
        "font-color",
        "text-style"
      ],
      correct: 1
    },
    {
      id: 3,
      text: "What does CSS stand for?",
      options: [
        "Creative Style Sheets",
        "Computer Style Sheets",
        "Cascading Style Sheets",
        "Colorful Style Sheets"
      ],
      correct: 2
    },
    {
      id: 4,
      text: "Which JavaScript keyword is used to declare a variable?",
      options: [
        "var",
        "let",
        "const",
        "All of the above"
      ],
      correct: 3
    },
    {
      id: 5,
      text: "What is the correct way to write a JavaScript array?",
      options: [
        "var colors = 'red', 'green', 'blue'",
        "var colors = (1:'red', 2:'green', 3:'blue')",
        "var colors = ['red', 'green', 'blue']",
        "var colors = {red, green, blue}"
      ],
      correct: 2
    },
    {
      id: 6,
      text: "Which HTML tag is used to link an external CSS file?",
      options: [
        "<css>",
        "<stylesheet>",
        "<link>",
        "<style>"
      ],
      correct: 2
    },
    {
      id: 7,
      text: "What does DOM stand for?",
      options: [
        "Document Object Model",
        "Data Object Model",
        "Document Oriented Model",
        "Display Object Management"
      ],
      correct: 0
    },
    {
      id: 8,
      text: "Which CSS property is used to make text bold?",
      options: [
        "font-bold",
        "text-bold",
        "font-weight",
        "bold-text"
      ],
      correct: 2
    },
    {
      id: 9,
      text: "What is the correct syntax for referring to an external script called 'script.js'?",
      options: [
        "<script href='script.js'>",
        "<script name='script.js'>",
        "<script src='script.js'>",
        "<script file='script.js'>"
      ],
      correct: 2
    },
    {
      id: 10,
      text: "Which company developed JavaScript?",
      options: [
        "Microsoft",
        "Netscape",
        "Google",
        "Apple"
      ],
      correct: 1
    }
  ];

  // Timer Effect
  useEffect(() => {
    if (!quizCompleted && !showResults && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0 && !quizCompleted && !showResults) {
      handleTimeOut();
    }
  }, [timeLeft, quizCompleted, showResults]);

  const handleTimeOut = () => {
    if (currentQuestion < questions.length - 1) {
      // Auto-move to next question
      setCurrentQuestion(currentQuestion + 1);
      setTimeLeft(30);
    } else {
      // Quiz completed due to timeout on last question
      calculateScore();
    }
  };

  const handleAnswerSelect = (optionIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: optionIndex
    });
    
    // Auto move to next question after 0.5 seconds
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setTimeLeft(30);
      } else {
        calculateScore();
      }
    }, 500);
  };

  const calculateScore = () => {
    let correctCount = 0;
    const review = [];
    
    questions.forEach((question, index) => {
      const userAnswer = selectedAnswers[index];
      const isCorrect = userAnswer === question.correct;
      if (isCorrect) correctCount++;
      
      review.push({
        question: question.text,
        userAnswer: userAnswer !== undefined ? question.options[userAnswer] : "No answer",
        correctAnswer: question.options[question.correct],
        isCorrect: isCorrect
      });
    });
    
    setScore(correctCount);
    setAnswersReview(review);
    setQuizCompleted(true);
    setShowResults(true);
  };

  const handleRetakeQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(30);
    setQuizCompleted(false);
    setShowResults(false);
    setScore(0);
    setAnswersReview([]);
  };

  const handleBackToCourses = () => {
    navigate('/browse-courses');
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (showResults) {
    const percentage = (score / questions.length) * 100;
    const passed = percentage >= 60;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Results Card */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className={`p-8 text-center ${passed ? 'bg-emerald-50' : 'bg-red-50'}`}>
              {passed ? (
                <Award className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
              ) : (
                <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
              )}
              <h2 className="text-3xl font-bold text-slate-800 mb-2">
                {passed ? "Congratulations!" : "Keep Learning!"}
              </h2>
              <p className="text-slate-600">
                {passed 
                  ? "You've successfully completed the Web Development assessment!" 
                  : "Don't worry! Review the material and try again."}
              </p>
            </div>

            {/* Score Section */}
            <div className="p-8 border-b border-slate-100">
              <div className="text-center mb-6">
                <div className="text-6xl font-bold text-blue-600 mb-2">
                  {score}/{questions.length}
                </div>
                <div className="text-lg text-slate-600">Your Score</div>
                <div className="mt-3 inline-block px-4 py-2 rounded-full text-sm font-bold" style={{
                  backgroundColor: passed ? '#d1fae5' : '#fee2e2',
                  color: passed ? '#065f46' : '#991b1b'
                }}>
                  {passed ? "PASSED" : "NOT PASSED"} ({percentage.toFixed(0)}%)
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-200 rounded-full h-2 mb-6">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${passed ? 'bg-emerald-500' : 'bg-red-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>

              {!passed && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <p className="text-amber-800 text-sm">
                    ⚠️ You need at least 60% to pass. Current score: {percentage.toFixed(0)}%
                  </p>
                </div>
              )}
            </div>

            {/* Answers Review */}
            <div className="p-8">
              <h3 className="text-xl font-bold text-slate-800 mb-6">Answer Review</h3>
              <div className="space-y-4">
                {answersReview.map((item, index) => (
                  <div key={index} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition">
                    <div className="flex items-start gap-3">
                      {item.isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-slate-800 mb-2">
                          Question {index + 1}: {item.question}
                        </p>
                        <div className="space-y-1 text-sm">
                          <p className={item.isCorrect ? 'text-emerald-700' : 'text-red-700'}>
                            Your answer: {item.userAnswer}
                          </p>
                          {!item.isCorrect && (
                            <p className="text-emerald-700">
                              Correct answer: {item.correctAnswer}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-8 bg-slate-50 flex gap-4">
              <button
                onClick={handleRetakeQuiz}
                className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition shadow-md"
              >
                Retake Quiz
              </button>
              <button
                onClick={handleBackToCourses}
                className="flex-1 py-3 px-6 border-2 border-slate-300 text-slate-700 rounded-xl font-bold hover:bg-slate-100 transition"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active Quiz View
  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Quiz Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{decodedCourseName}</h1>
              <p className="text-slate-500 text-sm">Skill Assessment Quiz</p>
            </div>
            <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full">
              <Clock className="w-4 h-4 text-slate-600" />
              <span className={`font-mono font-bold ${timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-2">
            <div className="flex justify-between text-sm text-slate-500 mb-1">
              <span>Progress</span>
              <span>{currentQuestion + 1} of {questions.length}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold">{currentQuestion + 1}</span>
              </div>
              <span className="text-slate-500 text-sm">Question</span>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 leading-relaxed">
              {currentQ.text}
            </h2>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {currentQ.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswerSelect(idx)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                  selectedAnswers[currentQuestion] === idx
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedAnswers[currentQuestion] === idx
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-slate-300'
                  }`}>
                    {selectedAnswers[currentQuestion] === idx && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-slate-700">{option}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Info Footer */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span>Select an answer to continue</span>
            </div>
            <div className="flex items-center gap-2">
              <span>⏱️ {timeLeft} seconds left</span>
            </div>
          </div>
        </div>

        {/* Timer Warning Modal when time is low */}
        {timeLeft <= 10 && timeLeft > 0 && (
          <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg animate-bounce">
            ⚠️ Hurry! Only {timeLeft} seconds left!
          </div>
        )}
      </div>
    </div>
  );
};

export default Webdevquiz;